import { Stack } from '@startuptools/ui/Stack';
import { Trans } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import {
  GqlAoaUpdateInput,
  GqlArticlesOfAssoc,
  GqlAuthorizationGroup,
  GqlMeeting,
  GqlMeetingConcern,
} from '../../../../graphql/base-types.graphql';
import { useAoaUpdateMutation, useCreateArticlesOfAssocMutation } from '../../../../graphql/react-operations';
import { StepSplit } from '../../../articles-of-assocs/components/steps/StepSplit';
import { RequireMeeting } from '../../../../components/RequireMeeting';
import { AuthorizationDocument } from '../../Authorization.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';

type FormData = Pick<GqlAoaUpdateInput, 'numberOfSharesAfter'> & { skipConcernUpdates?: boolean };

export const AgStepSplit = ({
  authorizationGroup,
}: {
  authorizationGroup: Pick<GqlAuthorizationGroup, 'id' | 'companyId'> & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id'> & {
          meeting: Pick<GqlMeeting, 'id' | 'kind'>;
        })
      | null;
    articlesOfAssoc?: Pick<
      GqlArticlesOfAssoc,
      'id' | 'changeNumberOfShares' | 'numberOfSharesBefore' | 'numberOfSharesAfter'
    > | null;
  };
}) => {
  const companyId = authorizationGroup.companyId;
  const { stepNext, stepTo } = useStepper();

  const [createAoa, { loading: createAoaLoading }] = useCreateArticlesOfAssocMutation({
    variables: {
      companyId,
      input: {
        meetingId: authorizationGroup.meetingConcern?.meeting.id,
        changeNumberOfShares: true,
      },
    },
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const [save, { loading }] = useAoaUpdateMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });
  const aoa = authorizationGroup.articlesOfAssoc;

  const onSave = async (input: FormData, stepNext?: () => Promise<unknown>) => {
    if (!aoa) {
      throw new Error('aoa is required');
    }
    try {
      await save({
        variables: {
          companyId,
          input: {
            ...input,
            id: aoa.id,
          },
        },
      });
      await stepNext?.();
    } catch (error) {
      captureException(error);
      genericErrorToast();
    }
  };

  return (
    <RequireMeeting
      meetingKind={authorizationGroup.meetingConcern?.meeting.kind}
      navigateToMeetingStep={() => stepTo('decision')}
      skip={stepNext}
      requiresUpdateMessage={
        <Trans id="Doing a split requires a decision from the shareholders meeting, but the meeting connected to this share issue is a board meeting. Update the meeting to do a split." />
      }
      requiresCreateMessage={
        <Trans id="Doing a split requires a decision from the shareholders meeting. Connect a meeting to do a split." />
      }
    >
      <Stack gap={3}>
        <Alert>
          <Trans id="If the company will carry out a stock split/merger in connection with the issuance of shares then it must be done here to ensure all calculations are correct. If no changes are to be made, simply skip this step." />
        </Alert>
        <StepSplit
          loading={loading || createAoaLoading}
          aoa={authorizationGroup.articlesOfAssoc}
          onSave={data =>
            onSave(
              { skipConcernUpdates: data.skipConcernUpdates, numberOfSharesAfter: data.numberOfSharesAfter },
              stepNext,
            )
          }
          onToggle={async (createSplit: boolean) => {
            try {
              if (createSplit && !aoa) {
                await createAoa();
                return;
              }
              if (!createSplit && aoa) {
                await onSave({ numberOfSharesAfter: aoa.numberOfSharesBefore });
              }
            } catch (error) {
              captureException(error);
              genericErrorToast();
            }
          }}
          key={aoa?.id ?? 'no-aoa'}
          stepNext={stepNext}
        />
      </Stack>
    </RequireMeeting>
  );
};
